import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent
} from "react";
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User
} from "firebase/auth";
import {
  applyKajikajyTheme,
  getInitialKajikajyTheme,
  persistKajikajyTheme,
  type KajikajyThemeMode
} from "@kajikajy/shared";
import {
  claimSupportAgentAccess,
  getSupportAttachmentUrl,
  getSupportTicket,
  listSupportTickets,
  replySupportTicket,
  setSupportTicketStatus,
  verifySupportSession,
  type SupportAttachment,
  type SupportMessage,
  type SupportStatus,
  type SupportTicketDetail,
  type SupportTicketSummary
} from "./lib/supportApi";
import { firebaseClientSettings, getKajikajyAdminFirebaseClient } from "./lib/firebaseClient";

const statusFilters: readonly (SupportStatus | "all")[] = [
  "all",
  "open",
  "in_progress",
  "waiting_user",
  "closed"
];

const statusLabels: Record<SupportStatus | "all", string> = {
  all: "Toutes",
  open: "Ouvertes",
  in_progress: "En cours",
  waiting_user: "Répondues",
  closed: "Fermées"
};

const emptyViewHeadings: Record<SupportStatus | "all", string> = {
  all: "Aucune demande",
  open: "Aucune demande ouverte",
  in_progress: "Aucune demande en cours",
  waiting_user: "Aucune demande répondue",
  closed: "Aucune demande fermée"
};

const categoryLabels: Record<string, string> = {
  general: "Fonctionnement général",
  sales: "Ventes",
  expenses: "Dépenses et obligations",
  stock: "Stocks",
  reports: "Rapports et documents",
  account: "Compte et accès",
  other: "Autre"
};

type TicketTarget = { ownerUid: string; ticketId: string };
type ConnectionState = "connecting" | "live" | "error";

const TICKET_REFRESH_INTERVAL_MS = 12_000;
const DETAIL_REFRESH_INTERVAL_MS = 5_000;

function ThemeToggle({
  theme,
  onToggle,
  className = ""
}: {
  theme: KajikajyThemeMode;
  onToggle: () => void;
  className?: string;
}) {
  const classes = ["kj-theme-toggle", className].filter(Boolean).join(" ");

  return (
    <button
      className={classes}
      type="button"
      aria-label={theme === "dark" ? "Activer le mode clair" : "Activer le mode sombre"}
      onClick={onToggle}
    >
      {theme === "dark" ? (
        <svg className="kj-theme-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      ) : (
        <svg className="kj-theme-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none">
          <path d="M12 3a6 6 0 0 0 9 6 9 9 0 1 1-9-6Z" />
        </svg>
      )}
    </button>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function requesterLabel(ticket: SupportTicketSummary): string {
  return (
    ticket.requesterEmail ??
    ticket.requesterPhoneNumber ??
    `Compte anonyme · ${ticket.ownerUid.slice(0, 8)}`
  );
}

function routeTicket(): TicketTarget | null {
  const match = window.location.pathname.match(/^\/tickets\/([^/]+)\/([a-f0-9]{32})$/);
  if (!match) return null;
  return {
    ownerUid: decodeURIComponent(match[1]),
    ticketId: match[2]
  };
}

function friendlyError(error: unknown): string {
  const code =
    typeof error === "object" && error && "code" in error
      ? String((error as { code?: unknown }).code ?? "").toLowerCase()
      : "";
  const message = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  const value = `${code} ${message}`;

  if (value.includes("permission-denied")) {
    return "Ce compte n’est pas autorisé à accéder aux demandes.";
  }
  if (value.includes("auth/invalid-credential")) {
    return "Adresse email ou mot de passe incorrect.";
  }
  if (value.includes("failed-precondition")) {
    return "Cette demande est déjà fermée.";
  }
  if (value.includes("not-found")) {
    return "Cette demande n’existe plus.";
  }
  if (value.includes("unavailable") || value.includes("network")) {
    return "Connexion interrompue. Reconnexion en cours…";
  }
  return "Impossible de charger les demandes pour le moment.";
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function establishSupportSession(user: User): Promise<void> {
  await claimSupportAgentAccess();
  await user.getIdToken(true);
  try {
    await verifySupportSession();
  } catch {
    await wait(900);
    await user.getIdToken(true);
    await verifySupportSession();
  }
}

function Attachment({ attachment }: { attachment: SupportAttachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    if (!attachment.storagePath) return;
    void getSupportAttachmentUrl(attachment.storagePath)
      .then((value) => {
        if (active) setUrl(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [attachment.storagePath]);

  if (failed) return <p className="attachment-state">Capture indisponible</p>;
  if (!url) return <p className="attachment-state">Chargement de la capture…</p>;
  return (
    <a className="attachment" href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Capture d’écran jointe au signalement" />
    </a>
  );
}

function Message({ message }: { message: SupportMessage }) {
  const fromSupport = message.sender === "support";
  return (
    <article className={fromSupport ? "message message--support" : "message message--user"}>
      <span>{fromSupport ? "Kajikajy" : "Utilisateur"}</span>
      <div className="message-body">
        <p>{message.body}</p>
        {message.attachment ? <Attachment attachment={message.attachment} /> : null}
      </div>
      <time>{formatDate(message.createdAt)}</time>
    </article>
  );
}

function SignIn({
  authError,
  onSignedIn,
  theme,
  onToggleTheme
}: {
  authError: string | null;
  onSignedIn: (user: User) => void;
  theme: KajikajyThemeMode;
  onToggleTheme: () => void;
}) {
  const client = getKajikajyAdminFirebaseClient();
  const [email, setEmail] = useState("support@kajikajy.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!client || loading) return;
    setLoading(true);
    setError(null);
    try {
      const credential = await signInWithEmailAndPassword(client.auth, email.trim(), password);
      onSignedIn(credential.user);
    } catch (cause) {
      setError(friendlyError(cause));
    } finally {
      setLoading(false);
    }
  }

  async function resetPassword() {
    if (!client || resetting) return;
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError("Indiquez l’adresse email du compte.");
      return;
    }
    setResetting(true);
    setError(null);
    setResetMessage(null);
    try {
      await sendPasswordResetEmail(client.auth, normalizedEmail);
    } catch {
      // Le message reste volontairement neutre pour ne pas révéler les comptes existants.
    } finally {
      setResetMessage("Si cette adresse est enregistrée, un email de réinitialisation a été envoyé.");
      setResetting(false);
    }
  }

  return (
    <div className="auth-shell kj-shell">
      <header className="auth-topbar">
        <a className="kj-brand" href="/" aria-label="Kajikajy">
          <img src="/kajikajy.png" alt="" />
          <span>Kajikajy</span>
        </a>
        <ThemeToggle theme={theme} onToggle={onToggleTheme} />
      </header>
      <main className="auth-page">
        <section className="auth-copy">
          <p className="eyebrow">ESPACE INTERNE</p>
          <h1>Messages et assistance</h1>
          <p>Consultez les demandes reçues depuis l’application et répondez aux utilisateurs.</p>
        </section>
        <form className="auth-form" onSubmit={submit}>
          <h2>Connexion</h2>
          <label className="kj-field">
            <span>Adresse email</span>
            <input
              autoComplete="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="kj-field">
            <span>Mot de passe</span>
            <input
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <button className="forgot-password" disabled={resetting} type="button" onClick={() => void resetPassword()}>
            {resetting ? "Envoi en cours…" : "Mot de passe oublié"}
          </button>
          {resetMessage ? <p className="form-note">{resetMessage}</p> : null}
          {error || authError ? <p className="form-error" role="alert">{error ?? authError}</p> : null}
          <button className="kj-button kj-button--primary kj-button--block" disabled={loading} type="submit">
            {loading ? "Vérification…" : "Se connecter"}
          </button>
        </form>
      </main>
    </div>
  );
}

export default function App() {
  const client = useMemo(() => getKajikajyAdminFirebaseClient(), []);
  const [theme, setTheme] = useState<KajikajyThemeMode>(getInitialKajikajyTheme);
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SupportStatus | "all">("all");
  const [tickets, setTickets] = useState<readonly SupportTicketSummary[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<TicketTarget | null>(() => routeTicket());
  const [selected, setSelected] = useState<SupportTicketDetail | null>(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const [listError, setListError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyKajikajyTheme(theme);
    persistKajikajyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    if (!client) {
      setAuthReady(true);
      return;
    }
    return onAuthStateChanged(client.auth, (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setAuthorized(false);
        setAuthReady(true);
        return;
      }
      setAuthReady(false);
      void establishSupportSession(nextUser)
        .then(() => {
          setAuthorized(true);
          setAuthReady(true);
          setAuthError(null);
        })
        .catch(async (cause) => {
          setAuthorized(false);
          setAuthError(friendlyError(cause));
          await signOut(client.auth).catch(() => undefined);
          setAuthReady(true);
        });
    });
  }, [client]);

  useEffect(() => {
    const handleHistory = () => setSelectedTarget(routeTicket());
    window.addEventListener("popstate", handleHistory);
    return () => window.removeEventListener("popstate", handleHistory);
  }, []);

  useEffect(() => {
    if (!authorized) {
      setTickets([]);
      setConnectionState("connecting");
      return;
    }

    let active = true;
    let running = false;
    const refreshTickets = async (showLoading = false) => {
      if (running) return;
      running = true;
      if (showLoading) setLoadingTickets(true);
      try {
        const nextTickets = await listSupportTickets("all");
        if (!active) return;
        setTickets(nextTickets);
        setConnectionState("live");
        setListError(null);
      } catch (cause) {
        if (!active) return;
        setConnectionState("error");
        setListError(friendlyError(cause));
      } finally {
        if (active) setLoadingTickets(false);
        running = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshTickets();
    };

    setConnectionState("connecting");
    setListError(null);
    void refreshTickets(true);
    const timer = window.setInterval(refreshWhenVisible, TICKET_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [authorized]);

  useEffect(() => {
    if (!authorized || !selectedTarget) {
      setSelected(null);
      setLoadingDetail(false);
      return;
    }

    const target = selectedTarget;
    let active = true;
    let running = false;
    const refreshDetail = async (showLoading = false) => {
      if (running) return;
      running = true;
      if (showLoading) setLoadingDetail(true);
      try {
        const detail = await getSupportTicket(target.ownerUid, target.ticketId);
        if (!active) return;
        setSelected(detail);
        setDetailError(null);
      } catch (cause) {
        if (!active) return;
        setDetailError(friendlyError(cause));
      } finally {
        if (active) setLoadingDetail(false);
        running = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshDetail();
    };

    setDetailError(null);
    void refreshDetail(true);
    const timer = window.setInterval(refreshWhenVisible, DETAIL_REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [authorized, selectedTarget]);

  const visibleTickets = useMemo(
    () => (filter === "all" ? tickets : tickets.filter((ticket) => ticket.status === filter)),
    [filter, tickets]
  );
  const unreadCount = useMemo(
    () => tickets.filter((ticket) => ticket.unreadForSupport).length,
    [tickets]
  );

  useEffect(() => {
    document.title = unreadCount > 0
      ? `(${unreadCount}) Demandes — Kajikajy`
      : "Demandes — Kajikajy";
  }, [unreadCount]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container || !selected) return;
    window.requestAnimationFrame(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    });
  }, [selected?.ticket.ticketId, selected?.messages.length]);

  function openTicket(ticket: SupportTicketSummary) {
    const target = { ownerUid: ticket.ownerUid, ticketId: ticket.ticketId };
    window.history.pushState(
      {},
      "",
      `/tickets/${encodeURIComponent(ticket.ownerUid)}/${ticket.ticketId}`
    );
    setSelectedTarget(target);
    setSelected(null);
    setActionError(null);
  }

  async function submitReply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected || !reply.trim() || submitting) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await replySupportTicket(
        selected.ticket.ownerUid,
        selected.ticket.ticketId,
        reply.trim()
      );
      setReply("");
      const [detail, nextTickets] = await Promise.all([
        getSupportTicket(selected.ticket.ownerUid, selected.ticket.ticketId),
        listSupportTickets("all")
      ]);
      setSelected(detail);
      setTickets(nextTickets);
    } catch (cause) {
      setActionError(friendlyError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  async function changeStatus(status: SupportStatus) {
    if (!selected || submitting) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await setSupportTicketStatus(selected.ticket.ownerUid, selected.ticket.ticketId, status);
      const [detail, nextTickets] = await Promise.all([
        getSupportTicket(selected.ticket.ownerUid, selected.ticket.ticketId),
        listSupportTickets("all")
      ]);
      setSelected(detail);
      setTickets(nextTickets);
    } catch (cause) {
      setActionError(friendlyError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  function closeConversation() {
    window.history.replaceState({}, "", "/");
    setSelectedTarget(null);
    setSelected(null);
    setActionError(null);
  }

  async function disconnect() {
    if (!client) return;
    await signOut(client.auth);
    closeConversation();
  }

  if (!firebaseClientSettings.isConfigured && !firebaseClientSettings.usesDemoConfig) {
    return <main className="center-state">Configuration Firebase absente.</main>;
  }
  if (!authReady) {
    return <main className="center-state">Ouverture de la console…</main>;
  }
  if (!user || !authorized) {
    return (
      <SignIn
        authError={authError}
        onSignedIn={setUser}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    );
  }

  const connectionLabel =
    connectionState === "live"
      ? unreadCount > 0
        ? `${unreadCount} ${unreadCount === 1 ? "nouveau message" : "nouveaux messages"}`
        : "À jour"
      : connectionState === "error"
        ? "Connexion interrompue"
        : "Connexion…";

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div className="admin-header-inner">
          <a className="kj-brand" href="/" aria-label="Kajikajy">
            <img src="/kajikajy.png" alt="" />
            <span>Kajikajy</span>
          </a>
          <div className="header-actions">
            <span className={`sync-indicator sync-indicator--${connectionState}`}>
              <i aria-hidden="true" />
              <span>{connectionLabel}</span>
            </span>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            <button className="kj-nav-link" type="button" onClick={() => void disconnect()}>
              Se déconnecter
            </button>
          </div>
        </div>
      </header>

      <div className="workspace">
        <aside className="ticket-pane">
          <div className="ticket-pane-heading">
            <div>
              <p className="eyebrow">ASSISTANCE</p>
              <h1>Demandes</h1>
            </div>
            <span>{visibleTickets.length}</span>
          </div>
          <nav className="filters" aria-label="Filtrer les demandes">
            {statusFilters.map((status) => (
              <button
                className={filter === status ? "active" : ""}
                key={status}
                type="button"
                onClick={() => setFilter(status)}
              >
                {statusLabels[status]}
              </button>
            ))}
          </nav>
          {listError ? <p className="workspace-error" role="alert">{listError}</p> : null}
          <div className="ticket-list">
            {loadingTickets && tickets.length === 0 ? (
              <p className="list-state">Synchronisation…</p>
            ) : visibleTickets.length === 0 ? (
              <div className="list-state">
                <strong>{emptyViewHeadings[filter]}</strong>
                <span>La liste se met à jour automatiquement.</span>
              </div>
            ) : (
              visibleTickets.map((ticket) => (
                <button
                  className={[
                    "ticket-row",
                    selected?.ticket.ticketId === ticket.ticketId ? "ticket-row--active" : "",
                    ticket.unreadForSupport ? "ticket-row--unread" : ""
                  ].filter(Boolean).join(" ")}
                  key={`${ticket.ownerUid}-${ticket.ticketId}`}
                  type="button"
                  onClick={() => openTicket(ticket)}
                >
                  <span className="ticket-row-topline">
                    <strong>{categoryLabels[ticket.category] ?? "Autre"}</strong>
                    {ticket.unreadForSupport ? <i aria-label="Nouveau message" /> : null}
                  </span>
                  <span className="ticket-identity">{requesterLabel(ticket)}</span>
                  <span className="ticket-preview">{ticket.lastMessagePreview}</span>
                  <span className="ticket-row-meta">
                    <time>{formatDate(ticket.updatedAt)}</time>
                    <em>{statusLabels[ticket.status]}</em>
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        <section className={selectedTarget ? "conversation-pane conversation-pane--open" : "conversation-pane"}>
          {loadingDetail ? (
            <div className="conversation-empty">Synchronisation de la conversation…</div>
          ) : detailError ? (
            <div className="conversation-empty">
              <p className="workspace-error" role="alert">{detailError}</p>
            </div>
          ) : !selected ? (
            <div className="conversation-empty" role="status" aria-live="polite">
              {loadingTickets && tickets.length === 0 ? (
                <>
                  <p className="eyebrow">ASSISTANCE</p>
                  <strong>Chargement des demandes</strong>
                  <p>La liste se synchronise automatiquement.</p>
                </>
              ) : visibleTickets.length === 0 ? (
                <>
                  <p className="eyebrow">
                    {filter === "all" ? "ASSISTANCE" : statusLabels[filter].toUpperCase()}
                  </p>
                  <strong>{emptyViewHeadings[filter]}</strong>
                  <p>
                    {filter === "all"
                      ? "Les nouvelles demandes apparaîtront ici automatiquement."
                      : "Sélectionnez une autre vue pour consulter les autres demandes."}
                  </p>
                </>
              ) : (
                <>
                  <p className="eyebrow">MESSAGERIE</p>
                  <strong>Sélectionnez une demande</strong>
                  <p>La conversation et les outils de réponse s’afficheront ici.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <header className="conversation-header">
                <button className="conversation-back" type="button" onClick={closeConversation}>
                  Demandes
                </button>
                <div>
                  <p className="eyebrow">{categoryLabels[selected.ticket.category] ?? "Autre"}</p>
                  <h2>{requesterLabel(selected.ticket)}</h2>
                  <span>Demande {selected.ticket.ticketId.slice(0, 8)}</span>
                </div>
                <label>
                  <span>Statut</span>
                  <select
                    disabled={submitting}
                    value={selected.ticket.status}
                    onChange={(event) => void changeStatus(event.target.value as SupportStatus)}
                  >
                    <option value="open">Ouverte</option>
                    <option value="in_progress">En cours</option>
                    <option value="waiting_user">Répondue</option>
                    <option value="closed">Fermée</option>
                  </select>
                </label>
              </header>
              {actionError ? <p className="workspace-error action-error" role="alert">{actionError}</p> : null}
              <div className="messages" ref={messagesRef}>
                {selected.messages.map((message) => (
                  <Message key={message.messageId} message={message} />
                ))}
              </div>

              {selected.ticket.status === "closed" ? (
                <footer className="closed-note">Cette demande est fermée.</footer>
              ) : (
                <form className="reply-form" onSubmit={submitReply}>
                  <label htmlFor="support-reply">Répondre</label>
                  <textarea
                    id="support-reply"
                    maxLength={4000}
                    placeholder="Écrivez votre réponse…"
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                  />
                  <div>
                    <span>{reply.length}/4000</span>
                    <button
                      className="kj-button kj-button--primary"
                      disabled={submitting || !reply.trim()}
                      type="submit"
                    >
                      {submitting ? "Envoi…" : "Envoyer"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </section>
      </div>
      <span className="sr-only" aria-live="polite">{connectionLabel}</span>
    </main>
  );
}
